import { useLinkTo } from '@react-navigation/native';
import { useGlobalConfig } from 'context/GlobalContext';
import { useBreakpointValueGS } from 'hooks/useBreakpointValueGS';
import React from 'react';
import { StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { useAnalytics } from 'services/analytics/useAnalytics';
import theme from 'theme';
import { AgeRatingKey } from 'utils/ageRating';
import { calcSizeForDevice, getUrlPath } from 'utils/index';
import { Platform } from 'utils/platform';

import CarouselImage from './coverImage';

import NewGradientButton from 'components/Button/NewGradienteButton';
import Rating from 'components/Rating';
import Skeleton from 'components/Skeleton';
import VideoPreview from 'components/VideoPreview';

type CarouselItemProps = {
  item: CmsHighlight;
  index: number;
  listId?: string;
  listName?: string;
  loading?: boolean;
  currentIndex?: number;
};

const CarouselItem: React.FC<CarouselItemProps> = ({
  item,
  index,
  listId,
  listName,
  loading = false,
  currentIndex = 0,
}) => {
  const linkTo = useLinkTo();
  const redirectLink = getUrlPath(item?.more?.link || '');
  const titleSize = useBreakpointValueGS({ xl: calcSizeForDevice(48, 54), lg: 54, base: 40 });
  const descriptionSize = useBreakpointValueGS({
    xl: calcSizeForDevice(24, 28),
    lg: 18,
    base: 14,
  });
  const lineHeightOfDescription = useBreakpointValueGS({
    xl: calcSizeForDevice(32),
    lg: 24,
    base: 18,
  });
  const { width: PAGE_WIDTH, height: PAGE_HEIGHT } = useWindowDimensions();
  const { isScrollingDown } = useGlobalConfig();
  const [isPreviewAllowed, setIsPreviewAllowed] = React.useState(false);
  const [isPreviewPlaying, setIsPreviewPlaying] = React.useState(false);
  const [watchedVideos, setWatchedVideos] = React.useState<number[]>([]);
  const videoPreviewUrl = item?.videoPreview;
  const analytics = useAnalytics();

  const handleButtonClick = () => {
    const analyticsPayload = {
      promotion_id: item?.id || item?._id || `banner_index_${index}`,
      promotion_name: item?.title || `Banner ${index + 1}`,
      creative_name: item?.image?.url || item?.image_vertical?.url || videoPreviewUrl || 'N/A',
      creative_slot: `slot_${(index || 0) + 1}`,
    };

    analytics.triggerSelectPromotionEvent({
      promotionName: analyticsPayload.promotion_name,
      promotionId: analyticsPayload.promotion_id,
      creativeName: analyticsPayload.creative_name,
      creativeSlot: analyticsPayload.creative_slot,
    });

    if (redirectLink) {
      linkTo(redirectLink);
    }
  };

  React.useEffect(() => {
    setIsPreviewPlaying(false);
    if (watchedVideos.includes(currentIndex)) {
      setIsPreviewAllowed(false);
    } else {
      setIsPreviewAllowed(true);
      const timer = setTimeout(() => {
        if (currentIndex === index) {
          setIsPreviewPlaying(true);
          setWatchedVideos((prevWatchedVideos) => [...prevWatchedVideos, currentIndex]);
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, index, watchedVideos]);

  React.useEffect(() => {
    if (Platform.OS === 'web' && !Platform.isTV && isScrollingDown) {
      setIsPreviewAllowed(false);
    }
  }, [isScrollingDown]);

  if (loading) return <Skeleton variant="hero" />;

  return (
    <View style={[styles.container, { justifyContent: PAGE_WIDTH > 500 ? undefined : 'flex-end' }]}>
      {item.image?.url && (
        <View style={styles.backgroundImage}>
          {PAGE_WIDTH > 500 ? (
            !Platform.isTV &&
            videoPreviewUrl &&
            isPreviewAllowed &&
            isPreviewPlaying &&
            currentIndex === index ? (
              <VideoPreview
                videoPreview={videoPreviewUrl}
                isPlaying={isPreviewPlaying}
                setIsPlaying={setIsPreviewPlaying}
                setIsAllowed={setIsPreviewAllowed}
              />
            ) : (
              <View style={styles.imageWrapper}>
                <View style={[styles.overlay, { borderRadius: theme.borderRadius['2xl'] }]} />
                <CarouselImage image={item.image} />
              </View>
            )
          ) : (
            <View style={styles.imageWrapper}>
              <View style={styles.overlay} />
              <CarouselImage image={item.image_vertical} />
            </View>
          )}
        </View>
      )}

      <View
        style={[
          {
            marginTop: PAGE_WIDTH > 500 ? 250 : 0,
            zIndex: 3,
            paddingHorizontal: PAGE_WIDTH > 500 ? 0 : 20,
            position: PAGE_WIDTH > 500 ? 'absolute' : undefined,
            left: PAGE_WIDTH > 500 ? 150 : 0,
            marginBottom: PAGE_WIDTH > 500 ? undefined : PAGE_HEIGHT < 680 ? 100 : 120,
          },
        ]}>
        <Text
          style={[
            styles.title,
            {
              fontSize: titleSize,
              lineHeight: calcSizeForDevice(64, 60),
              marginBottom: PAGE_WIDTH > 500 ? 20 : 0,
            },
          ]}>
          {item?.title}
        </Text>

        {item?.rating && <Rating ageRatingKey={item.rating as AgeRatingKey} />}

        <Text
          style={[
            styles.description,
            {
              fontSize: descriptionSize,
              lineHeight: lineHeightOfDescription,
              marginVertical: calcSizeForDevice(20, 15),
            },
          ]}>
          {item?.description}
        </Text>

        {item?.more?.title && (
          <View
            style={[
              styles.ctaButtonContainer,
              { marginHorizontal: PAGE_WIDTH > 500 ? undefined : 'auto' },
            ]}>
            <NewGradientButton onPress={() => handleButtonClick()} style={styles.ctaButton}>
              <Text style={styles.ctaButtonText}>{item?.more?.title || 'Assistir agora'}</Text>
            </NewGradientButton>
          </View>
        )}
      </View>
    </View>
  );
};

export default CarouselItem;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
  },

  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
  },
  imageWrapper: {
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
  },
  overlay: {
    position: 'absolute',
    backgroundColor: '#00000080',
    zIndex: 99999,
    width: '100%',
    height: '100%',
  },
  contentDesktop: {
    top: 0,
  },
  contentMobile: {
    left: 0,
    right: 0,
    paddingLeft: 20,
    paddingRight: 20,
  },
  title: {
    color: '#FFF',
    fontWeight: '600', // $semibold
    fontFamily: 'system-ui',
  },
  description: {
    color: '#FFF',
    fontWeight: '400',
    fontFamily: 'system-ui',
    width: '70%',
  },
  ctaButton: {
    height: 54,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  ctaButtonContainer: {
    width: 300,
  },
  ctaButtonText: {
    fontWeight: 'bold',
    color: '#070d31',
    textTransform: 'uppercase',
  },
});
